import React, { useEffect, useState } from "react";

import { ShieldCheck } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import { ComplianceService } from "@/services/complianceService";
import Skeleton from "@/components/shared/Skeleton";
import ChecklistReadOnlyView from "@/pages/admin/compliance/checklists/components/ChecklistReadOnlyView";

/**
 * The checklist this job's candidates are offered by DEFAULT (Phase D).
 *
 * A recruiter sending someone to compliance used to be asked which document
 * checklist applied, at send time, and they do not know — the person who does
 * knew it when they wrote the checklist and had nowhere to record it. This tab
 * shows what they recorded.
 *
 * **Now a tab, and it renders the checklist itself.** It used to be a card
 * above the tab bar with an eye button that opened the builder read-only, so
 * the answer to "what will we ask this candidate for" was a modal away, on a
 * screen that also happens to be the editor. As a tab beside Job Description it
 * shows the sections and documents inline, and the eye is gone because there is
 * nothing left for it to reveal.
 *
 * The three states below are distinct on purpose. A tab the user deliberately
 * opened must never render nothing: as a card, "still loading", "no checklist"
 * and "compliance is down" could all collapse into an absent card nobody
 * missed. In a tab each of those reads as a broken screen, so each says what it
 * is.
 *
 * **A default, not an assignment.** Candidates on one job do not all need the
 * same evidence: three may want the nursing checklist and a fourth something
 * else. So the copy here says "by default" and says the recruiter can still
 * change it, because a card that reads as a binding rule would be a lie about
 * what the send screen does.
 *
 * The card is wrapped in `<ModuleGate>` by its parent rather than checking
 * entitlement itself. That is deliberate and documented on ModuleGate: React
 * does not run a child's effects when the child is not rendered, and **that is
 * what stops the fetch**. A component that mounts and returns early has already
 * called the endpoint and already shown an unentitled tenant an error about a
 * feature they never bought.
 *
 * Wiring a checklist to a job is a `COMPLIANCE_MANAGE` act and happens in the
 * checklist builder — one place, one owner. This card only reads.
 */
const JobComplianceChecklistCard = ({ jobId }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let active = true;
    setLoading(true);
    ComplianceService.getJobChecklist(jobId)
      .then((res) => {
        if (!active) return;
        setState(res ?? null);
        setFailed(false);
      })
      .catch((e) => {
        // Non-fatal by design, matching the compliance indicator on the
        // candidate profile: a compliance outage must not break the job detail
        // screen. Named rather than swallowed, though — a read that failed is
        // not a job with no checklist, and saying "none assigned" for an outage
        // would be a confident lie.
        console.error(e);
        if (!active) return;
        setState(null);
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [jobId]);

  const heading = (
    <div className="mb-3 flex items-center gap-2">
      <ShieldCheck size={15} className="text-text-secondary" aria-hidden="true" />
      <h3 className="text-[13px] font-semibold text-text-primary">Compliance checklist</h3>
    </div>
  );

  if (loading) {
    return (
      <div>
        {heading}
        <Skeleton className="h-5 w-56" />
        <Skeleton className="mt-2 h-4 w-80" />
        <Skeleton className="mt-4 h-28 w-full" />
      </div>
    );
  }

  if (failed) {
    return (
      <div>
        {heading}
        <p className="text-[13px] text-text-primary/60">Could not load the compliance checklist for this job. Try again shortly.</p>
      </div>
    );
  }

  const { checklistId, checklistName } = state ?? {};

  if (!checklistId) {
    return (
      <div>
        {heading}
        <p className="text-[13px] font-medium text-text-primary">No compliance checklist assigned.</p>
        <p className="mt-0.5 text-[12px] text-text-primary/70">
          Candidates for this job will need a checklist chosen by hand when they are sent to compliance. Contact your compliance team to have one assigned.
        </p>
      </div>
    );
  }

  return (
    <div>
      {heading}
      <p className="text-[13px] font-medium text-text-primary">{checklistName}</p>
      <p className="mt-0.5 text-[12px] text-text-primary/70">
        Candidates for this job are sent to compliance with this checklist by default. Whoever sends them can still choose a different one.
      </p>
      <ChecklistReadOnlyView checklistId={checklistId} className="mt-4" />
    </div>
  );
};

/**
 * The gate lives here so every caller gets it, and so the panel itself never
 * mounts — and therefore never fetches — for a tenant without Compliance.
 *
 * `passthrough`: a killed module must not make this card look broken. The
 * default `control` variant would mark it `inert` with a tooltip, which is
 * right for a button and wrong for a read-only panel whose content is still
 * true.
 */
const GatedJobComplianceChecklistCard = (props) => (
  <ModuleGate module={MODULES.COMPLIANCE} variant="passthrough">
    <JobComplianceChecklistCard {...props} />
  </ModuleGate>
);

export default GatedJobComplianceChecklistCard;
